import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';
import { GlobalPositionStrategy, Overlay } from '@angular/cdk/overlay';
import { DialogRef } from './dialog-ref';
import { DIALOG_DATA } from './dialog-tokens';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from './dialogs/confirmation-dialog/confirmation-dialog.component';
import { DialogConfig } from '@angular/cdk/dialog';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private overlay = inject(Overlay);
  private injector = inject(Injector);

  open<R = boolean, T = unknown>(
    component: ComponentType<T>,
    config?: DialogConfig,
    customPositionStrategy?: GlobalPositionStrategy,
  ): DialogRef<R> {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayRef = this.overlay.create({
      positionStrategy: customPositionStrategy ?? positionStrategy,
      hasBackdrop: true,
      disposeOnNavigation: true,
      backdropClass: 'overlay-backdrop',
      panelClass: customPositionStrategy
        ? 'overlay-sheet-panel'
        : 'overlay-panel',
    });
    const dialogRef = new DialogRef<R>(overlayRef);

    const injector = Injector.create({
      parent: this.injector,
      providers: [
        {
          provide: DialogRef,
          useValue: dialogRef,
        },
        {
          provide: DIALOG_DATA,
          useValue: config?.data,
        },
      ],
    });

    // Attach component portal to the overlay
    const portal = new ComponentPortal(component, null, injector);
    overlayRef.attach(portal);
    return dialogRef;
  }

  openConfirmationDialog(config: { data: ConfirmationDialogData }): DialogRef {
    return this.open(ConfirmationDialogComponent, config);
  }

  openAsSheet<R = boolean, T = unknown>(
    component: ComponentType<T>,
    config?: DialogConfig,
  ) {
    const positionStrategy = this.overlay.position().global().end('0');

    return this.open<R, T>(component, config, positionStrategy);
  }

  openAsFullscreen<R = boolean, T = unknown>(
    component: ComponentType<T>,
    config?: DialogConfig,
  ) {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally();

    return this.open<R, T>(component, config, positionStrategy);
  }
}
